import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaRobot, 
  FaCode, 
  FaMobileAlt, 
  FaCloud, 
  FaBug,
  FaArrowRight,
  FaCogs,
  FaDatabase
} from 'react-icons/fa';
import gsap from 'gsap';

const ServicesSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    if (sectionRef.current) {
      const ctx = gsap.context(() => {
        gsap.to(".service-grid-bg", {
          backgroundPosition: "100% 100%",
          duration: 40,
          repeat: -1,
          ease: "none",
        });
      }, sectionRef);

      return () => ctx.revert();
    }
  }, []);

  const services = [
    {
      slug: 'ai-automation',
      title: 'AI & Automation',
      description: 'Build adaptive AI agents and intelligent automation solutions that transform business operations.',
      icon: FaRobot,
      features: ['Machine Learning', 'NLP Solutions', 'Process Automation', 'Computer Vision'],
    },
    {
      slug: 'product-engineering',
      title: 'Product Engineering',
      description: 'Full-stack teams that ship scalable products at unicorn velocity with modern architectures.',
      icon: FaCode,
      features: ['Full-Stack Development', 'Microservices', 'API Design', 'Scalable Architecture'],
    },
    {
      slug: 'mobile-development',
      title: 'Mobile Development',
      description: 'Native iOS and Android apps with React Native and Flutter for exceptional user experiences.',
      icon: FaMobileAlt,
      features: ['Cross-Platform', 'Native Performance', 'App Store Optimization', 'Real-time Sync'],
    },
    {
      slug: 'cloud-devops',
      title: 'Cloud & DevOps',
      description: 'Scalable cloud infrastructure and DevOps practices for seamless deployment and operations.',
      icon: FaCloud,
      features: ['AWS/Azure/GCP', 'CI/CD Pipelines', 'Infrastructure as Code', 'Kubernetes'],
    },
    {
      slug: 'qa-testing',
      title: 'QA & Testing',
      description: 'End-to-end testing and quality assurance for all your products with automated workflows.',
      icon: FaBug,
      features: ['Automated Testing', 'Performance Testing', 'Security Audits', 'CI Integration'],
    },
    {
      slug: 'data-engineering',
      title: 'Data Engineering',
      description: 'Build robust data pipelines and analytics solutions to drive informed business decisions.',
      icon: FaDatabase,
      features: ['Data Warehousing', 'Real-time Analytics', 'ETL Pipelines', 'Data Visualization'],
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-24 lg:py-32 overflow-hidden bg-transparent"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div 
          className="service-grid-bg absolute inset-0"
          style={{
            backgroundImage: `
              radial-gradient(circle at 1px 1px, rgba(255,255,255,0.02) 1px, transparent 0)
            `,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-gray-800 to-gray-950 rounded-full blur-3xl opacity-20" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-br from-gray-900 to-black rounded-full blur-3xl opacity-20" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 lg:mb-20">
          {/* Section Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-900 rounded-full blur-md opacity-50" />
              <div className="relative flex items-center gap-2 px-4 py-2 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-full">
                <FaCogs className="text-gray-400 text-sm" />
                <span className="text-xs font-medium tracking-wider text-gray-300 uppercase">
                  What We Offer
                </span>
              </div>
            </div>
          </div>

          {/* Section Title */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
            <span className="text-white">Comprehensive</span>
            <br />
            <span className="bg-gradient-to-r from-gray-300 via-gray-100 to-gray-400 bg-clip-text text-transparent">
              Technical Solutions
            </span>
          </h2>

          {/* Section Description */}
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            From concept to deployment, we provide end-to-end development services 
            that help businesses scale and innovate in the digital age.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ delay: index * 0.1, duration: 0.65, ease: [0.25, 0.1, 0.25, 1] }}
              whileHover={{ y: -8, transition: { duration: 0.22 } }}
              className="group relative"
            >
              {/* Main Card */}
              <div className="relative h-full bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 transition-all duration-300 hover:border-gray-600 hover:bg-gray-900/80 hover:-translate-y-2">
                {/* Icon Container */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-900 rounded-2xl blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
                  <div className="relative w-16 h-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 flex items-center justify-center group-hover:border-gray-600 transition-colors duration-300">
                    <service.icon className="text-3xl text-gray-300 group-hover:text-white transition-colors duration-300" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-white mb-3">
                  {service.title}
                </h3>
                
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features List */}
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-500">
                      <div className="w-1 h-1 bg-gradient-to-r from-gray-400 to-gray-600 rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Learn More Link */}
                <Link
                  to={`/services/${service.slug}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-gray-400 group-hover:text-white transition-colors duration-300"
                >
                  Learn More
                  <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 lg:mt-20">
          <a
            href="/services"
            className="group relative px-8 py-4 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 inline-flex items-center gap-2 border border-gray-700 hover:border-gray-600 hover:scale-105"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="relative flex items-center gap-2">
              Explore All Services
              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </span>
          </a>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />
    </section>
  );
};

export default ServicesSection;